import { useEffect, useRef } from "react";
import type { ChatMessage } from "../hooks/chat-message";
import { MessageBubble } from "./message-bubble";

interface MessageListProps {
  messages: ChatMessage[];
  activeMsgId: string | null;
  onCitePillClick: (citationNumber: number) => void;
  onActivateMessage: (msgId: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  activeMsgId,
  onCitePillClick,
  onActivateMessage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMsgCountRef = useRef(0);
  const wasStreamingRef = useRef(false);
  // 마지막 스트리밍 업데이트 시점에 사용자가 바닥 근처였는지 기억.
  // 완료 순간에는 높이가 이미 급변한 뒤라 그 시점의 스크롤 위치로는
  // 사용자 의도를 판단할 수 없으므로, 직전 프레임의 값을 쓴다.
  const wasNearBottomRef = useRef(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 마지막 봇 메시지의 스트리밍 완료(true→false) 감지.
    // 완료 순간 Streamdown 이 streaming→static 으로 바뀌며 숨겼던 마크다운을
    // 펼치고 normalizeAnswer 로 내용이 재구성돼 높이가 급변한다. nearBottom
    // 임계값에 걸려 하단이 잘리므로, 레이아웃 확정 후 강제로 바닥까지 스크롤한다.
    const isStreaming = messages.some((m) => m.isStreaming);
    const justFinishedStreaming = wasStreamingRef.current && !isStreaming;
    wasStreamingRef.current = isStreaming;

    if (messages.length > prevMsgCountRef.current) {
      // 새 메시지 추가 → 항상 스크롤
      prevMsgCountRef.current = messages.length;
      wasNearBottomRef.current = true;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (justFinishedStreaming) {
      // 스트리밍 완료 → 사용자가 바닥 근처에 있던 경우에만, 높이 급변 후
      // 확정된 레이아웃 기준으로 바닥까지 스크롤 (위로 올려 읽던 중이면 유지)
      if (!wasNearBottomRef.current) return;
      const rafId = requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      return () => cancelAnimationFrame(rafId);
    }

    // 스트리밍 content 업데이트 → 바닥 근처일 때만 스크롤
    const nearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      80;
    wasNearBottomRef.current = nearBottom;
    if (nearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-5 py-5">
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          message={msg}
          isActive={msg.id === activeMsgId}
          onCitePillClick={onCitePillClick}
          onActivate={() => onActivateMessage(msg.id)}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  );
};
